import { List, Record } from '@gitbook/util-immutable';

import { isType, MODEL_TYPES } from '../constants';
// Prevents a circular dependency
import { invertOperation } from '../operations/invert';
import { arrayEqual } from '../utils';
import type { Operation } from './operation';
import type { TextRange } from './text-range';

interface HistoryProperties {
    /**
     * A stack of inverted operations that are pushed whenever a user performs
     * an action in the editor, and popped whenever a user performs an undo().
     **/
    undos: List<OperationsBatch>;
    /**
     * A stack of operations that are pushed whenever a user performs an undo(),
     * by taking the popped operations from `redos`, and cleared when a user performs
     * a new edit in the document.
     **/
    redos: List<OperationsBatch>;
}

interface OperationsBatchProperties {
    operations: List<Operation>;
    timestamp: number;
    selectionBefore: TextRange | null;
}

/** Maximum time between operations in the same batch */
const MAX_TIME_BETWEEN_OPERATIONS = 1000;

export class OperationsBatch extends Record<OperationsBatchProperties>({
    operations: List.create(),
    timestamp: 0,
    selectionBefore: null,
}) {
    declare public readonly operations: List<Operation>;
    declare public readonly timestamp: number;
    declare public readonly selectionBefore: TextRange | null;

    /**
     * Create a new `OperationsBatch` from a set of operations.
     */
    public static create(batch: {
        selectionBefore: TextRange | null;
        operations: Operation[] | List<Operation>;
    }): OperationsBatch {
        return OperationsBatch.createRecord({
            selectionBefore: batch.selectionBefore,
            operations: List.create(batch.operations),
            timestamp: Date.now(),
        });
    }

    /**
     * Push operations into the batch.
     * The new operations are inserted at the beginning of the batch to prepare for the order of application
     * when undoing.
     */
    public push(operations: Operation[]): OperationsBatch {
        return this.merge({
            operations: this.operations.unshift(...operations),
            timestamp: Date.now(),
        });
    }

    /**
     * Check whether to merge a new operation `o` into this batch.
     */
    public shouldMerge(o: Operation): boolean {
        const p = this.operations.first();
        if (!p) {
            return false;
        }

        if (o.type === 'set_selection' && p.type === 'set_selection') {
            return true;
        }

        const now = Date.now();
        const timeBetween = now - this.timestamp;
        if (timeBetween > MAX_TIME_BETWEEN_OPERATIONS) {
            // We don't merge into one history batch if the user waited between the changes
            return false;
        }

        if (
            o.type === 'insert_text' &&
            p.type === 'insert_text' &&
            p.offset === o.offset + o.text.length &&
            arrayEqual(o.path as unknown[], p.path as unknown[])
        ) {
            return true;
        }

        if (
            o.type === 'remove_text' &&
            p.type === 'remove_text' &&
            p.offset + p.text.length === o.offset &&
            arrayEqual(o.path as unknown[], p.path as unknown[])
        ) {
            return true;
        }

        return false;
    }
}

/**
 * History of changes in an editor.
 */
export class History extends Record<HistoryProperties>({
    undos: List.create(),
    redos: List.create(),
}) {
    declare public readonly undos: HistoryProperties['undos'];
    declare public readonly redos: HistoryProperties['redos'];

    /*
     * Object.
     */
    get object() {
        return 'history';
    }

    /**
     * Check if `input` is a `History`.
     */
    public static isHistory(input: any): input is History {
        return isType('HISTORY', input);
    }

    /**
     * Create a new `History` from a set of operations.
     */
    public static create(properties?: Partial<HistoryProperties>): History {
        return History.createRecord(properties);
    }

    /**
     * Save a batch of operations into the history.
     */
    public save(state: {
        /**
         * Selection before the operations were applied.
         */
        selectionBefore: TextRange;

        /**
         * Operations to save in a reversed order (and inverted).
         */
        operations: Operation[];

        /**
         * If false, operations will be saved as a new batch.
         */
        merge?: boolean;
    }): History {
        const { selectionBefore, operations, merge = true } = state;

        if (process.env.NODE_ENV !== 'production') {
            const setSelectionOperation = operations.some(
                (operation) => operation.type === 'set_selection'
            );
            if (setSelectionOperation) {
                throw new Error('set_selection operations should not be saved in the history');
            }
        }

        let { undos, redos } = this;

        const lastOperation = operations[operations.length - 1];
        const prevBatch = undos.first();

        // If the `merge` flag is true, add the operation to the previous batch.
        if (merge && prevBatch && prevBatch.shouldMerge(lastOperation)) {
            const batch = prevBatch.push(operations);
            undos = undos.shift();
            undos = undos.unshift(batch);
        } else {
            // Otherwise, create a new batch with the operation.
            const batch = OperationsBatch.create({ operations, selectionBefore });
            undos = undos.unshift(batch);
        }

        // Constrain the history to 100 entries for memory's sake.
        if (undos.size > 100) {
            undos = undos.take(100);
        }

        // Clear the redos and update the history.
        redos = redos.clear();
        return this.merge({ undos, redos });
    }

    /**
     * Undo the last changes (return operations to be applied).
     */
    public undo():
        | undefined
        | {
              /** Operations to apply */
              operations: List<Operation>;
              /** New selection */
              selection: TextRange | null;
              /** New history */
              history: History;
          } {
        let { undos, redos } = this;
        const previous = undos.first();
        if (!previous) {
            return undefined;
        }

        // Shift the previous operations into the redo stack.
        undos = undos.shift();
        redos = redos.unshift(previous);

        return {
            history: this.merge({ undos, redos }),
            operations: previous.operations,
            selection: previous.selectionBefore,
        };
    }

    /**
     * Redo the last undoed changes (return operations to be applied).
     */
    public redo():
        | undefined
        | {
              /** Operations to apply */
              operations: List<Operation>;
              /** New selection */
              selection: TextRange | null;
              /** New history */
              history: History;
          } {
        let { undos, redos } = this;
        const next = redos.first();
        if (!next) {
            return undefined;
        }

        // Shift the next value into the undo stack.
        redos = redos.shift();
        undos = undos.unshift(next);

        // Replay the next operations that were pushed when a user performed an `undo()`.
        // Moreover, we only invert the operations now to avoid doing unnecessary `invert`
        // calls when there are good chances that users won't perform a `redo()` more often
        // than `undo()`.
        const operations = next.operations.slice(0).reverse().map(invertOperation);

        return {
            history: this.merge({ undos, redos }),
            operations,
            selection: next.selectionBefore,
        };
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

History.prototype[MODEL_TYPES.HISTORY] = true;
